// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { navigate } from "@reach/router";
import { Button, Divider, Form, message, Modal, Popconfirm } from "antd";
import { QuestionCircleOutlined, TeamOutlined } from "@ant-design/icons";
import {
  NewsWriteRequestDTO,
  SettingsInfoResponseDTO
} from "../../../../api/backend-openapi";
import { requestCreateNews } from "../../../../state/news/NewsEffects";
import { addNews } from "../../../../state/news/NewsEvents";
import { formItemBlockLayout, NEWS_PRIORITY } from "../../../../utils/Constant";
import {
  Block,
  Image,
  NewsWorkflowStatus,
  PreviewFields
} from "../../../types/NewsTypes";
import NewsContentInputFormSection from "../details/NewsContentInputFormSection";
import { NewsBlocks } from "../block/NewsBlocks";
import NewsImageForm from "../details/NewsImageForm";
import { SaveOutlined, StopOutlined } from "@ant-design/icons/lib";
import { validate } from "../../../../validator/NewsValidator";
import { requestGetSettings } from "../../../../state/settings/SettingsEffects";
import NewsEditionPreviewButton from "../details/preview/NewsEditionPreviewButton";
import { getBase64UrlFrom } from "../../../../utils/ImageUtils";
import { ValidatorResult } from "../../../types/Types";
import NewsSettingsFormSection from "../details/NewsSettingsFormSection";
import { displayError } from "../../../../utils/displayError";

const { confirm } = Modal;

const AddNewsForm: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [settings, setSettings] = useState<SettingsInfoResponseDTO | null>(
    null
  );

  const [branch, setBranch] = useState<string | undefined>(undefined);

  const [canSendPush, setCanSendPush] = useState(true);

  const [sendPushValueToDisplay, setSendPushValueToDisplay] = useState(true);

  const [
    hideFromHomepageValueToDisplay,
    setHideFromHomepageValueToDisplay
  ] = useState(false);

  const [priorityOkForPush, setPriorityOkForPush] = useState(true);
  const [
    scheduledAvailabilityOkForPush,
    setScheduledAvailabilityOkForPush
  ] = useState(true);

  const [buttonLoading, setButtonLoading] = useState<boolean>(false);

  const [blocks, setBlocks] = useState<Block[]>([]);

  const [mainImage, setMainImage] = useState<Image | undefined>(undefined);

  const [previewFields, setPreviewFields] = useState<PreviewFields>({});

  const confirmationPopup = async () => {
    return new Promise<boolean>(resolve => {
      confirm({
        title: t("global.confirm"),
        content: t("add_news.modal.description"),
        okText: t("global.yes"),
        cancelText: t("global.no"),
        centered: true,
        onOk() {
          resolve(true);
        },
        onCancel() {
          resolve(false);
        }
      });
    });
  };

  useEffect(() => {
    return requestCreateNews.done.watch(({ result }) => {
      setButtonLoading(false);
      if (result.ok && result.data) {
        addNews(result.data);
        navigate(`/news/${result.data.id}`);
        message.success(t("add_news.success"));
      } else {
        displayError(result.errorMessage || "Error!", false);
      }
    });
  });

  useEffect(() => {
    requestGetSettings({});
  }, []);

  useEffect(() => {
    return requestGetSettings.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setSettings(result.data);
      }
    });
  });

  const handleSubmit = (values: any, status: NewsWorkflowStatus) => {
    const validationResult: ValidatorResult = validate(blocks);
    if (!validationResult.validate) {
      displayError(validationResult.message, false);
    } else {
      confirmationPopup().then((confirmed: boolean) => {
        if (confirmed && mainImage) {
          blocks.forEach(block => delete block.idTempLocal);
          const newsToAdd: NewsWriteRequestDTO = {
            title: values.title,
            priority: values.priority,
            scheduledAvailabilityDate: values.scheduledAvailabilityDate,
            scheduledUnpublishDate: values.scheduledUnpublishDate,
            summary: values.summary,
            newsThemeId: values.newsThemeId,
            keywordIds: values.keywords,
            mainImageData: mainImage.base64Data,
            mainImageFilename: mainImage.name,
            mainImageType: mainImage.type,
            content: JSON.stringify(blocks),
            sendPush: sendPushValueToDisplay,
            eventStartDate: values.eventDates?.[0],
            eventEndDate: values.eventDates?.[1],
            status,
            audience: values.audience,
            broadcastListId: values.broadcastListId,
            hideFromHomepage: values.hideFromHomepage
          };
          requestCreateNews(newsToAdd);
          setButtonLoading(true);
        }
      });
    }
  };

  const handleSave = (values: any) => {
    handleSubmit(values, NewsWorkflowStatus.SAVED);
  };

  const handleSubmitToValidation = () => {
    form
      .validateFields()
      .then(values => {
        handleSubmit(values, NewsWorkflowStatus.VALIDATION);
      })
      .catch(() => {
        displayError("global.on_finish_failed");
      });
  };

  const onFinishFailed = ({ errorFields }: any) => {
    displayError("global.on_finish_failed");
    form.scrollToField(errorFields[0].name);
  };

  return (
    <Form
      {...formItemBlockLayout}
      onFinish={handleSave}
      onFinishFailed={onFinishFailed}
      initialValues={{
        priority: NEWS_PRIORITY[0].value
      }}
      form={form}
    >
      <div className="form-first-item-sticky-top-container">
        <div className="form-first-item-sticky-top-content">
          <Divider orientation="left">
            <h3 className="text-secondary mb-0">{t("news.news_actions")}</h3>
          </Divider>
          <Form.Item className="text-center mb-0">
            <Popconfirm
              title={t("add_news.cancel_confirmation")}
              okText={t("global.yes")}
              cancelText={t("global.no")}
              onConfirm={() => {
                navigate("/news");
                form.resetFields();
              }}
              placement="top"
              icon={<QuestionCircleOutlined />}
            >
              <Button htmlType="reset" className="m-2">
                <StopOutlined /> {t("add_news.cancel")}
              </Button>
            </Popconfirm>
            <NewsEditionPreviewButton
              newsTitle={form.getFieldValue("title")}
              newsSummary={form.getFieldValue("summary")}
              newsImage={
                mainImage
                  ? getBase64UrlFrom(mainImage.base64Data, mainImage.type)
                  : ""
              }
              blocks={blocks}
              iconButton={false}
              newsThemeId={form.getFieldValue("newsThemeId")}
              newsEventStartDate={form.getFieldValue("eventDates")?.[0]}
              newsEventEndDate={form.getFieldValue("eventDates")?.[1]}
            />
            <Button
              className="btn-primary m-2"
              htmlType="submit"
              loading={buttonLoading}
            >
              <SaveOutlined /> {t("global.save")}
            </Button>
            <Button
              className="btn-success m-2"
              onClick={handleSubmitToValidation}
              loading={buttonLoading}
            >
              <TeamOutlined /> {t("edit_news.validation")}
            </Button>
          </Form.Item>
        </div>
      </div>
      <Divider orientation="left">
        <h3 className="text-secondary mb-0">{t("news.settings")}</h3>
      </Divider>
      <NewsSettingsFormSection
        showRequired={true}
        editMode={true}
        previewFields={previewFields}
        setPreviewFields={setPreviewFields}
        branch={branch}
        setBranch={setBranch}
        canSendPush={canSendPush}
        setCanSendPush={setCanSendPush}
        sendPushValueToDisplay={sendPushValueToDisplay}
        setSendPushValueToDisplay={setSendPushValueToDisplay}
        hideFromHomepageValueToDisplay={hideFromHomepageValueToDisplay}
        setHideFromHomepageValueToDisplay={setHideFromHomepageValueToDisplay}
        priorityOkForPush={priorityOkForPush}
        setPriorityOkForPush={setPriorityOkForPush}
        scheduledAvailabilityOkForPush={scheduledAvailabilityOkForPush}
        setScheduledAvailabilityOkForPush={setScheduledAvailabilityOkForPush}
        resetNewsTheme={() => {
          form.setFields([
            {
              name: "newsThemeId",
              value: null
            },
            {
              name: "priority",
              value: NEWS_PRIORITY[0].value
            }
          ]);
        }}
      />
      <Divider orientation="left">
        <h3 className="text-secondary mb-0">{t("news.news_content")}</h3>
      </Divider>
      <NewsImageForm
        formFieldName="mainImage"
        formFieldLabel={t("add_news.main_image")}
        rules={{
          width: settings?.newsMainImageWidth,
          height: settings?.newsMainImageHeight,
          size: settings?.newsMainImageSize
        }}
        setImage={setMainImage}
      />
      <NewsContentInputFormSection
        previewFields={previewFields}
        setPreviewFields={setPreviewFields}
      />
      <NewsBlocks blocks={blocks} setBlocks={setBlocks} />
    </Form>
  );
};

export default AddNewsForm;
